import { type JSX, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { deviceListMsg, deviceListsMsg, omaElisaForCompaniesMsg, t } from '../../common/i18n/index.js';
import { resetErrors } from '../../selfservice/actions/index.js';
import { BreadCrumbs } from '../BreadCrumbs/index.js';
import { CatalogProductsSummary } from '../CatalogProductsSummary/CatalogProductSummary.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { Loading } from '../Loading/index.js';
import { CatalogDraftOptions } from './CatalogDraftOptions';
import { CatalogInformation } from './CatalogInformation';
import { CatalogManageButtonGroup } from './CatalogManageButtonGroup';
import { CatalogPublishedDetails } from './CatalogPublishedDetails';
import { DiscontinuedCatalogsDisclaimer } from './DiscontinuedCatalogsDisclaimer';
import { EditDiscontinuedDevices } from './EditDiscontinuedDevices';
import type { DialogParams } from '../../common/types/dialog.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type { BillingAccountHeader, Catalog, VirtualCatalog } from '../../generated/api/models.js';

import './CatalogDetails.scss';

export interface CatalogDetailsProps {
  virtualCatalog: VirtualCatalog;
  catalog: Catalog;
  products: Record<string, Array<CatalogProduct>>;
  productsPublished?: Record<string, Array<CatalogProduct>>;
  companyName: string;
  onAddMoreProducts: () => void;
  billingAccounts: BillingAccountHeader[];
  accountMasterId: string;
}

export const CatalogDetails = ({
  catalog,
  virtualCatalog,
  products,
  productsPublished,
  companyName,
  onAddMoreProducts,
  billingAccounts,
  accountMasterId,
}: CatalogDetailsProps) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const navigate = useNavigate();
  const onShowDialog = (params: DialogParams) => navigate(search.toString(), { state: params });
  const [viewPublished, setViewPublished] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(resetErrors());
    };
  });

  const breadCrumbs: JSX.Element = (
    <BreadCrumbs
      breadCrumbPaths={[
        { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
        { name: t.COBB(deviceListsMsg), path: paths.COMPANY_INFO_CATALOGS },
        { name: catalog.name },
      ]}
    />
  );

  if (viewPublished && virtualCatalog.published && productsPublished) {
    return (
      <CatalogPublishedDetails
        breadCrumbs={breadCrumbs}
        publishedDate={virtualCatalog.published?.lastModified}
        billingAccount={
          billingAccounts.find(ba => ba.billingAccountId === virtualCatalog.published?.billingAccountId)
            ?.billingAccountDisplayId || '-'
        }
        catalog={virtualCatalog.published}
        products={productsPublished}
        companyName={companyName}
        onClickBack={() => setViewPublished(false)}
        mdmId={accountMasterId}
      />
    );
  } else {
    return (
      <DetailsWrapper
        detailsTop={breadCrumbs}
        id={`catalog-details-${catalog.catalogCode}`}
        classes={[`of-catalog-details`]}
        heading={catalog.name}
        headingBottom={companyName}
        headingTop={t.Z0HF(deviceListMsg)}
        heroPicto="billing-account"
      >
        <DiscontinuedCatalogsDisclaimer discontinuedProducts={catalog.discontinuedProducts ?? []} />
        <CatalogDraftOptions
          catalog={catalog}
          virtualCatalog={virtualCatalog}
          viewPublished={viewPublished}
          setViewPublished={setViewPublished}
        />
        <CatalogInformation
          virtualCatalog={virtualCatalog}
          billingAccounts={billingAccounts}
          catalog={catalog}
          mdmId={accountMasterId}
        />
        <CatalogManageButtonGroup
          catalog={catalog}
          virtualCatalog={virtualCatalog}
          onShowDialog={onShowDialog}
          onAddMoreProducts={onAddMoreProducts}
          accountMasterId={accountMasterId}
        />

        <EditDiscontinuedDevices catalog={catalog} virtualCatalog={virtualCatalog} mdmId={accountMasterId} />
        {!products || catalog.productCategories.some(value => !products[value]) ? (
          // load all category products first and then show product summary
          <Loading />
        ) : (
          <CatalogProductsSummary {...{ products }} />
        )}
      </DetailsWrapper>
    );
  }
};
